import React, { useMemo } from 'react';
import { Alert, Box } from '@mui/material';

import DetailPanelHeader from './DetailPanelHeader';
import DetailPanelProductSelectionsSection from './DetailPanelProductSelectionsSection';
import DetailPanelStatusGrid from './DetailPanelStatusGrid';
import useApi from '../../../hooks/useApi';
import { resolveProgramName } from '../../../utils/dataNormalizer';
import { sumOrderItemsMrr } from '../appsheet/appsheetMrrUtils';

// DetailPanelSelectionsTab is the primary operational tab inside the dealer
// detail drawer. It combines dealer header context, status summary, and the
// product-selections table into the default investigation/editing workspace.
const DetailPanelSelectionsTab = ({
  currentRow,
  programLabel,
  programCode,
  programKey,
  isHydratingDetails,
  isRefreshing,
  refreshDisabled,
  onRefresh,
  enrollmentStatusMeta,
  apiPresenceStatusMeta,
  productStatusMeta,
  terminationStatusMeta,
  liveSelectionsMeta,
  programConsentMeta,
  dataConsentMeta,
  buildStatusChip,
  formatDate,
  productSelections,
  userEmail,
  onNavigateToDealer,
  onRowClick,
  onNotify,
}) => {
  const { useAppsheetDealerContext } = useApi();
  const { data: appSheetData } = useAppsheetDealerContext(currentRow?.bac);
  const lastUpdateDisplay = formatDate(currentRow?.lastUpdateDate ?? currentRow?.lastUpdateDt);
  const websiteLinks = useMemo(() => {
    const normalizeUrl = (value) => {
      const trimmed = String(value || '').trim();
      if (!trimmed) return '';
      if (/^https?:\/\//i.test(trimmed)) return trimmed;
      return `https://${trimmed}`;
    };
    const candidates = [
      { label: 'Buick Website', value: currentRow?.buickUrl },
      { label: 'Cadillac Website', value: currentRow?.cadillacUrl },
      { label: 'Chevrolet Website', value: currentRow?.chevroletUrl },
      { label: 'GMC Website', value: currentRow?.gmcUrl },
    ];
    return candidates
      .map((candidate) => ({ label: candidate.label, url: normalizeUrl(candidate.value) }))
      .filter((candidate) => Boolean(candidate.url));
  }, [currentRow?.buickUrl, currentRow?.cadillacUrl, currentRow?.chevroletUrl, currentRow?.gmcUrl]);

  const bacMrrDisplay = useMemo(() => {
    const selections = Array.isArray(productSelections) ? productSelections : [];
    if (selections.length === 0) {
      return '$0.00';
    }

    const normalizeKey = (value) => String(value ?? '').trim().replace(/[\s-]+/g, '_').toUpperCase();
    const isBillingRow = (row) => {
      const raw = row?.isBilling ?? row?.billingStatus ?? row?.billingFlag ?? row?.billing ?? row?.is_billing ?? null;
      if (raw === true) return true;
      if (raw === false || raw === null || raw === undefined) return false;
      const key = normalizeKey(raw);
      return key === 'TRUE' || key === 'YES' || key === 'Y' || key === '1' || key === 'BILLING';
    };
    const parsePrice = (value) => {
      if (value === null || value === undefined || value === '') return 0;
      if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
      const cleaned = String(value).replace(/[^0-9.-]+/g, '');
      if (!cleaned) return 0;
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    // BAC MRR is derived from the DPE-side product selection rows, not AppSheet.
    const total = selections.reduce((sum, row) => {
      if (!isBillingRow(row)) return sum;
      const rawPrice = row?.dealerPrice ?? row?.dealerPriceAmount ?? row?.dealerPriceAmt ?? row?.price ?? row?.dealerFee ?? row?.dealerPriceDisplay;
      return sum + parsePrice(rawPrice);
    }, 0);

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(total);
  }, [productSelections]);

  const asMrrDisplay = useMemo(() => {
    const programName = resolveProgramName(
      currentRow?.categoryCd ||
        currentRow?.programName ||
        currentRow?.programLabel ||
        currentRow?.programCode ||
        programLabel ||
        programCode
    );
    if (!programName) {
      return '$0.00';
    }

    // AS MRR gives operators the AppSheet-side counterpart so they can compare
    // the DPE view against AppSheet/DI billing context at a glance.
    const rows = Array.isArray(appSheetData?.orderItems) ? appSheetData.orderItems : [];
    const total = sumOrderItemsMrr(rows, { targetProgram: programName });

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(total);
  }, [appSheetData?.orderItems, currentRow, programCode, programLabel]);

  return (
    <Box
      data-testid="detail-selections-tab"
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2,
        minHeight: 0,
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        touchAction: 'pan-y',
        overscrollBehavior: 'contain',
      }}
    >
      {isHydratingDetails ? (
        <Alert severity="info">Loading dealer selections for BAC {currentRow?.bac}…</Alert>
      ) : null}

      <DetailPanelHeader
        dealerName={currentRow.dealerName}
        bac={currentRow.bac}
        programLabel={programLabel}
        programCode={programCode}
        lastUpdateDisplay={lastUpdateDisplay}
        bacMrrDisplay={bacMrrDisplay}
        asMrrDisplay={asMrrDisplay}
        isRefreshing={isRefreshing}
        refreshDisabled={refreshDisabled || !programKey}
        onRefresh={onRefresh}
        websiteLinks={websiteLinks}
      />

      <DetailPanelStatusGrid
        currentRow={currentRow}
        enrollmentStatusMeta={enrollmentStatusMeta}
        apiPresenceStatusMeta={apiPresenceStatusMeta}
        productStatusMeta={productStatusMeta}
        terminationStatusMeta={terminationStatusMeta}
        liveSelectionsMeta={liveSelectionsMeta}
        programConsentMeta={programConsentMeta}
        dataConsentMeta={dataConsentMeta}
        buildStatusChip={buildStatusChip}
        formatDate={formatDate}
      />

      <DetailPanelProductSelectionsSection
        productSelections={productSelections}
        userEmail={userEmail}
        onNavigateToDealer={onNavigateToDealer}
        onRowClick={onRowClick}
        onNotify={onNotify}
        enableVmtQuickActions
      />
    </Box>
  );
};

export default DetailPanelSelectionsTab;
